import React, { useState } from "react";
import { Typography, Chip, Avatar } from "@mui/material";
import CustomTable from "../../../components/Table/Table";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useClientIndustriesViewModel } from "./ClientIndustryViewModel";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { StyledCardContent } from "./ClientIndustryStyles";
import { useLoading } from "../../../contexts/LoadingContext";
import Modal from "../../../components/Modal/Modal";
import ConfirmationModal from "../../../components/ConfirmationModal/ConfirmationModal";
import BusinessIcon from "@mui/icons-material/Business";
import CircleIcon from "@mui/icons-material/Circle";
import FactoryIcon from "@mui/icons-material/Factory";
import PublicIcon from "@mui/icons-material/Public";
import AddClientIndustry from "../AddClientIndustry/AddClientIndustry";
import { useRbac } from "../../../hooks/RbacContext";

const ClientIndustries = () => {
  const {
    clientIndustries,
    loading,
    error,
    handleDelete,
    createClientIndustry,
    updateClientIndustry,
    refetch,
    handleSearch,
  } = useClientIndustriesViewModel();

  const { showLoading, hideLoading } = useLoading();

  const [isAddClientIndustryOpen, setIsAddClientIndustryOpen] = useState(false);
  const [selectedClientIndustry, setSelectedClientIndustry] = useState(null);
  const [successModal, setSuccessModal] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState(null);
  const {checkAccess} = useRbac();

  const columns = [
    {
      id: "industry_name",
      label: "Name",
    },
    {
      id: "created_on",
      label: "Created On",
      align: "left",
    },

    {
      id: "updated_on",
      label: "Updated On",
      align: "left",
    },
  ];
  const fixedColumns = {
    left: [],
    right: ["actions"],
  };
  const handleAddClientIndustry = () => {
    setSelectedClientIndustry(null);
    setIsAddClientIndustryOpen(true);
  };

  const handleEditClientIndustry = (clientIndustry) => {
    setSelectedClientIndustry(clientIndustry);
    setIsAddClientIndustryOpen(true);
  };

  const handleCloseDialog = () => {
    setIsAddClientIndustryOpen(false);
    setSelectedClientIndustry(null);
  };

  const handleSubmit = async (formData) => {
    try {
      showLoading();
      if (selectedClientIndustry) {
        await updateClientIndustry(
          selectedClientIndustry.client_industry_id,
          formData
        );
        setSuccessModal({
          type: "success",
          title: "Success",
          message: "Client Industry updated successfully",
        });
      } else {
        await createClientIndustry(formData);
        setSuccessModal({
          type: "success",
          title: "Success",
          message: "Client Industry added successfully",
        });
      }
      handleCloseDialog();
    } catch (error) {
      throw error;
    } finally {
      hideLoading();
    }
  };

  const handleCloseSuccessModal = () => {
    setSuccessModal(null);
  };

  const handleDeleteClick = (clientIndustry) => {
    setDeleteConfirmation({
      clientIndustry,
      open: true,
    });
  };

  const handleDeleteConfirm = async () => {
    const clientIndustry = deleteConfirmation.clientIndustry;
    try {
      showLoading();
      await handleDelete(clientIndustry);
      setDeleteConfirmation(null);
      setSuccessModal({
        type: "success",
        title: "Success",
        message: "Client Industry deleted successfully",
      });
    } catch (error) {
      setSuccessModal({
        type: "error",
        title: "Action Failed",
        message: error.message || "Failed to delete client industry",
      });
    } finally {
      hideLoading();
    }
  };

  const getActions = (clientIndustry) => {

    const actions = [];
    if (checkAccess("clientIndustry", "update")) {
      actions.push({
        icon: <EditIcon fontSize="small" />,
        label: "Edit",
        onClick: handleEditClientIndustry,
        color: "primary",
        sx: {
          "&:hover": {
            backgroundColor: (theme) =>
              theme.palette.primary.lighter || "rgba(33, 150, 243, 0.08)",
          },
        },
      });
    }
    if (checkAccess("clientIndustry", "delete")) {
      actions.push({
        icon: <DeleteIcon fontSize="small" />,
        label: "Delete",
        onClick: handleDeleteClick,
        color: "error",
        sx: {
          "&:hover": {
            backgroundColor: (theme) =>
              theme.palette.error.lighter || "rgba(211, 47, 47, 0.08)",
          },
        },
      });
    }

    return actions;
  };

  if (error) {
    return (
      <StyledCardContent>
        <Typography color="error">Error: {error}</Typography>
      </StyledCardContent>
    );
  }
  const columnFilters = [
    {
      id: "industry_name",
      type: "select",
      label: "Client Industry Name",
      icon: <BusinessIcon />,
      sendIcon: true,
    },
  ];

  return (
    <div>
      <CustomTable
        columns={columns}
        data={clientIndustries}
        onAdd={handleAddClientIndustry}
        showActions={true}
        showAddButton={checkAccess("clientIndustry","add")}
        addButtonIcon={<PersonAddIcon />}
        showSearch={true}
        showColumnVisibility={false}
        pageSize={10}
        stickyHeader={true}
        fixedColumns={fixedColumns}
        actions={getActions}
        title="Client Industries"
        subtitle="Manage and monitor all client industries"
        addButtonText="Add Client Industry"
        alignment="left"
        fontSize="12px"
        showFilters={true}
        columnFilters={columnFilters}
      />

      <AddClientIndustry
        open={isAddClientIndustryOpen}
        onClose={handleCloseDialog}
        initialData={selectedClientIndustry}
        onSubmit={handleSubmit}
      />

      {successModal && (
        <Modal
          type={successModal.type}
          title={successModal.title}
          message={successModal.message}
          onClose={handleCloseSuccessModal}
        />
      )}

      {deleteConfirmation && (
        <ConfirmationModal
          open={true}
          title="Delete Client Industry"
          message={`Are you sure you want to delete client industry "${deleteConfirmation.clientIndustry.client_industry_id}"?`}
          onClose={() => setDeleteConfirmation(null)}
          onConfirm={handleDeleteConfirm}
        />
      )}
    </div>
  );
};

export default ClientIndustries;
